/**
 * BodyMapBlock — renders a `body_map` content block via the shared BodyMapView.
 *
 * Supports two rendering paths:
 *
 * 1. Canonical (hydrated) — when `front_tags`/`back_tags` are present (added by S2
 *    hydration in XoeOrchestrationService). Passes a BodyMapOutput to BodyMapView so
 *    the full analysis-style rendering (front + back tags, anatomical correlates,
 *    inline detail panel) is used.
 *
 * 2. Legacy / model-fallback — only the flat `tags: XoeBodyTag[]` is present.
 *    BodyMapView coerces them internally (low→normal, medium→warning, high→critical)
 *    and renders without anatomical correlates.
 *
 * External props (unchanged — XoeBlockRenderer and its tests rely on this shape):
 *   tags        — XoeBodyTag[]  (always populated; back-compat for legacy blocks)
 *   gender      — optional 'male' | 'female'
 *   front_tags  — optional ComprehensiveBodyTag[] (present when hydrated)
 *   back_tags   — optional ComprehensiveBodyTag[] (present when hydrated)
 *   anatomical_correlates — optional AnatomicalCorrelate[] (present when hydrated)
 *   hydrated    — optional boolean; when true the block was hydrated from a
 *                 completed PatientAnalysis even if front/back tags are empty.
 *                 Guards the empty-but-completed analysis case (body_map with
 *                 empty front_tags+back_tags but real anatomical_correlates, or
 *                 a body_map that was genuinely mapped with zero tag locations).
 */

import React from 'react';

import { BodyMapView } from '../../shared/BodyModel';
import type { BodyMapBlockProps } from '../../../types/xoe/XoeUiTypes';

const BodyMapBlock: React.FC<BodyMapBlockProps> = ({
	tags,
	gender,
	front_tags,
	back_tags,
	anatomical_correlates,
	hydrated,
}) => {
	// isHydrated is true when:
	//   (a) the backend explicitly marked the block as hydrated from a completed
	//       PatientAnalysis (hydrated=true), OR
	//   (b) front/back tags are non-empty (original tag-count guard — preserved
	//       for blocks produced before the hydrated field was added), OR
	//   (c) anatomical_correlates are non-empty (an analysis may carry correlates
	//       even if no body-map tag locations were assigned).
	// This ensures a completed analysis whose body_map has empty front+back tags
	// routes through the canonical BodyMapView path (matching the analysis view)
	// rather than silently falling to the "No body map data" legacy stub.
	const isHydrated =
		Boolean(hydrated) ||
		(front_tags?.length ?? 0) > 0 ||
		(back_tags?.length ?? 0) > 0 ||
		(anatomical_correlates?.length ?? 0) > 0;

	if (isHydrated) {
		return (
			<BodyMapView
				bodyMap={{
					front_tags:            front_tags ?? [],
					back_tags:             back_tags ?? [],
					anatomical_correlates: anatomical_correlates ?? [],
				}}
				gender={gender}
			/>
		);
	}

	return (
		<BodyMapView
			legacyTags={tags ?? []}
			gender={gender}
		/>
	);
};

export default BodyMapBlock;
